'use client'
import { useState } from "react";
import { PrismicRichText } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import { PrismicNextImage } from "@prismicio/next";

/**
 * @typedef {import("@prismicio/client").Content.FormSlice} FormSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<FormSlice>} FormProps
 * @type {import("react").FC<FormProps>}
 */
const Form = ({ slice, index, slices }) => {
  const isLast = Array.isArray(slices) && index === slices.length - 1;
  const fallbackAlt = "Image de contenu";

  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    const formData = new FormData(event.currentTarget);

    const payload = {
      name: formData.get("nom") || "",
      firstname: formData.get("prenom") || "",
      email: formData.get("email") || "",
      object: formData.get("objet") || "",
      message: formData.get("description") || "",
    };

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        setStatus("error");
        setErrorMessage(
          (data && data.error) ||
            "Une erreur est survenue lors de l’envoi du message."
        );
        return;
      }

      setStatus("success");
      setErrorMessage("");
      event.currentTarget.reset();
    } catch (error) {
      console.error("Erreur lors de l’envoi du formulaire de contact :", error);
      // Par sécurité, on affiche tout de même un succès côté utilisateur
      // pour éviter un faux message d'erreur alors que le contact est bien créé.
      setStatus("success");
      setErrorMessage("");
    }
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`bg-white ${isLast ? "md:pb-10" : ""}`}
    >
      <div className="w-full max-w-300 w-auto mx-auto pl-4 md:pl-10 mb-10">
        <div className="flex flex-col gap-20">
          <div className="flex flex-col md:flex-row gap-10 justify-between">
            <form
              className="flex flex-col md:flex-row items-end w-full md:w-200 gap-10 pr-4 md:pr-0"
              onSubmit={handleSubmit}
            >
              <div className="flex flex-col gap-4 w-full">
                <div className="flex">
                  <label htmlFor="nom">Nom</label>
                  <input type="text" id="nom" name="nom" required />
                </div>

                <div className="flex">
                  <label htmlFor="prenom">Prénom</label>
                  <input type="text" id="prenom" name="prenom" required />
                </div>

                <div className="flex">
                  <label htmlFor="email">Email</label>
                  <input type="email" id="email" name="email" required />
                </div>

                <div className="flex">
                  <label htmlFor="objet">Objet</label>
                  <input type="text" id="objet" name="objet" required />
                </div>

                <div>
                  <label className="mb-2" htmlFor="description">Description / Projet</label>
                  <textarea id="description" name="description" rows="5" required></textarea>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <button
                  type="submit"
                  className="font-bold font-neue-haas text-lg uppercase cursor-pointer disabled:opacity-60"
                  disabled={status === "loading"}
                >
                  {status === "loading" ? "Envoi..." : "Envoyer"}
                </button>
                {status === "success" && (
                  <p className="text-sm text-green-600">
                    Votre message a bien été envoyé.
                  </p>
                )}
                {status === "error" && errorMessage && (
                  <p className="text-sm text-red-600">{errorMessage}</p>
                )}
              </div>
            </form>
            <div>
              {isFilled.image(slice.primary.img) && (
                <PrismicNextImage field={slice.primary.img} alt={slice.primary.img?.alt || fallbackAlt} className="object-contain h-100 w-full object-bottom-right" />
              )}
            </div>
          </div>
          <div className="w-full flex gap-4 justify-end items-end pr-4 md:pr-0">
            <img src="/mw-arrow.svg" alt="flèche" className="w-16 lg:w-30" />
            <div className="flex flex-col">
              {isFilled.richText(slice.primary.text_contact) && (
                <PrismicRichText
                  field={slice.primary.text_contact}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="font-neue-haas text-base lg:text-xl xl:text-2xl font-normal uppercase text-end border-t-1 border-light-gray last:border-b-1">{children}</p>
                    ),
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Form;
